'use client';

import { useParams } from 'next/navigation';
import { motion } from 'framer-motion';
import ScrollReveal from '@/app/components/animations/ScrollReveal';
import PixelSquares from '@/app/components/ui/PixelSquares';
import { TbAlertTriangle as AlertTriangle, TbAward as Award, TbFileText as FileText, TbMessage as MessageSquare, TbRefresh as RefreshCw, TbScale as Scale } from 'react-icons/tb';

const content = {
    en: {
        eyebrow: 'Legal Agreement',
        title: 'Terms of Service',
        subtitle: 'Please review these Terms of Service. They govern your use of our platforms, services, and communication systems at nibras Agency.',
        lastUpdated: 'Last Updated: May 2026',
        sections: [
            {
                icon: FileText,
                title: '1. Agreement Acceptance',
                body: 'By accessing, browsing, or interacting with nibras.agency, submitting contact forms, or employing nibras Digital Marketing Agency services, you accept and agree to be bound by these Terms of Service. If you disagree, you must immediately discontinue site and service usage.',
            },
            {
                icon: Award,
                title: '2. Intellectual Property Rights',
                body: 'All custom materials, brand designs, photography, cinematic films, source code, strategy proposals, and copy developed by nibras Agency remain our exclusive intellectual property until legally transferred or licensed to the client upon final invoice settlement.',
            },
            {
                icon: AlertTriangle,
                title: '3. Acceptable Use Policy',
                body: 'You agree to use our digital assets and forms strictly for legitimate business inquiries. Submitting fraudulent leads, scraping site content, deploying automated bot submissions, or executing malicious injection scripts on our contact endpoints is strictly prohibited and subject to legal action.',
            },
            {
                icon: Scale,
                title: '4. Limitation of Liability',
                body: 'nibras Digital Marketing Agency and its associates shall not be liable for any direct, indirect, incidental, or consequential damages resulting from your use or inability to use the site, database submission delays, temporary service interruptions, or force majeure events.',
            },
            {
                icon: RefreshCw,
                title: '5. Amendments & Updates',
                body: 'We reserve the right to revise, update, or modify these Terms of Service at any time without prior individual notice. Your continued interaction with our platforms after updates are posted constitutes your immediate acceptance of the revised legal terms.',
            },
            {
                icon: MessageSquare,
                title: '6. Legal Inquiries',
                body: 'For questions, contract clarifications, or standard inquiries regarding our legal frameworks and project agreement policies, please contact our legal relations division at info@nibras.agency.',
            },
        ],
    },
    ar: {
        eyebrow: 'الاتفاقية القانونية',
        title: 'شروط الخدمة',
        subtitle: 'يرجى مراجعة شروط الخدمة هذه. هي تحكم استخدامك لمنصاتنا وخدماتنا وأنظمة التواصل الخاصة بنا في وكالة نبراس.',
        lastUpdated: 'آخر تحديث: مايو 2026',
        sections: [
            {
                icon: FileText,
                title: '1. قبول الاتفاقية',
                body: 'بدخولك وتصفحك لموقع nibras.agency، أو تقديم نماذج التواصل، أو الاستعانة بخدمات وكالة نبراس للتسويق الرقمي، فإنك توافق على الالتزام بشروط الخدمة هذه. إذا كنت لا توافق، يجب عليك التوقف فوراً عن استخدام الموقع والخدمات.',
            },
            {
                icon: Award,
                title: '2. حقوق الملكية الفكرية',
                body: 'تظل جميع المواد المخصصة، وتصاميم الهوية، والصور الفوتوغرافية، والأفلام السينمائية، والأكواد البرمجية، والمقترحات الاستراتيجية، والنصوص التي طورتها وكالة نبراس ملكاً فكرياً حصرياً لنا حتى يتم نقلها أو ترخيصها قانونياً للعميل عند التسوية النهائية للفواتير.',
            },
            {
                icon: AlertTriangle,
                title: '3. سياسة الاستخدام المقبول',
                body: 'أنت توافق على استخدام أصولنا ونماذجنا الرقمية بدقة وبشكل خاص للاستفسارات التجارية المشروعة. يُمنع منعاً باتاً تقديم طلبات وهمية، أو كشط محتوى الموقع، أو استخدام روبوتات إرسال تلقائية، أو تشغيل نصوص برمجية ضارة على منصاتنا للتواصل.',
            },
            {
                icon: Scale,
                title: '4. تحديد المسؤولية',
                body: 'لن تكون وكالة نبراس للتسويق الرقمي أو شركاؤها مسؤولين عن أي أضرار مباشرة أو غير مباشرة أو عرضية أو تبعية تنتج عن استخدامك أو عدم قدرتك على استخدام الموقع، أو تأخر إرسال البيانات، أو انقطاع الخدمة المؤقت، أو الظروف القاهرة.',
            },
            {
                icon: RefreshCw,
                title: '5. التعديلات والتحديثات',
                body: 'نحتفظ بالحق في مراجعة أو تحديث أو تعديل شروط الخدمة هذه في أي وقت دون إشعار مسبق. استمرار تفاعلك مع منصاتنا بعد نشر التحديثات يشكل قبولاً فورياً منك للشروط القانونية المعدلة.',
            },
            {
                icon: MessageSquare,
                title: '6. الاستفسارات القانونية',
                body: 'للأسئلة أو الاستفسارات المتعلقة بأطرنا القانونية وسياسات اتفاقيات المشاريع الخاصة بنا، يرجى التواصل مع قسم العلاقات القانونية لدينا عبر البريد الإلكتروني info@nibras.agency.',
            },
        ],
    },
};

export default function TermsPage() {
    const params = useParams();
    const locale = (params?.locale as 'en' | 'ar') || 'ar';
    const isAr = locale === 'ar';
    const t = content[locale];

    return (
        <main 
            className="min-h-screen relative overflow-hidden py-32 px-6 bg-brand-bg text-brand-text" 
            dir={isAr ? 'rtl' : 'ltr'}
        >
            {/* Elegant Background Gradients & Elements */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(234,50,40,0.12),transparent_35rem),radial-gradient(circle_at_80%_70%,rgba(34,131,186,0.12),transparent_35rem)]" />
            
            <PixelSquares
                pattern="steps"
                tone="orange"
                className="-left-10 top-36 rotate-90"
                opacityClassName="opacity-15"
                sizeClassName="w-40 md:w-72"
            />
            
            <PixelSquares
                pattern="scatter"
                tone="mixed"
                className="-right-10 bottom-24"
                opacityClassName="opacity-[0.12]"
                sizeClassName="w-36 md:w-56"
            />

            <div className="max-w-4xl mx-auto relative z-10">
                {/* Header Section */}
                <div className="text-center mb-20">
                    <ScrollReveal mode="fade-up">
                        <p className="mb-5 text-sm font-bold uppercase tracking-[0.22em] text-brand-cta">
                            {t.eyebrow}
                        </p>
                        <h1 className="text-5xl md:text-7xl font-heading font-bold mb-8">
                            {t.title}
                        </h1>
                    </ScrollReveal>
                    
                    <ScrollReveal mode="fade-up" delay={0.2}>
                        <p className="mx-auto max-w-2xl font-body text-lg md:text-xl leading-relaxed opacity-70 mb-4">
                            {t.subtitle}
                        </p>
                        <span className="inline-block px-4 py-1.5 rounded-full bg-brand-text/5 border border-brand-text/10 text-xs font-semibold opacity-60 font-body">
                            {t.lastUpdated}
                        </span>
                    </ScrollReveal>
                </div>

                {/* Terms Content Blocks with glassmorphic cards */}
                <div className="space-y-8">
                    {t.sections.map((section, idx) => {
                        const IconComponent = section.icon;
                        return (
                            <ScrollReveal 
                                key={idx} 
                                mode="fade-up" 
                                delay={0.1 * idx}
                                className="p-8 md:p-10 bg-brand-text/[0.03] border border-brand-text/10 rounded-3xl backdrop-blur-md hover:border-brand-text/20 transition-all duration-300 group shadow-lg"
                            >
                                <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
                                    <div className="p-4 rounded-2xl bg-brand-cta/10 text-brand-cta group-hover:bg-brand-cta group-hover:text-white transition-all duration-300 shadow-md">
                                        <IconComponent className="w-8 h-8" />
                                    </div>
                                    <div className="space-y-4 flex-1">
                                        <h2 className="text-2xl font-bold font-heading text-brand-text">
                                            {section.title}
                                        </h2>
                                        <p className="text-brand-text/80 font-body text-base leading-relaxed">
                                            {section.body}
                                        </p>
                                    </div>
                                </div>
                            </ScrollReveal>
                        );
                    })}
                </div>
            </div>
        </main>
    );
}
